import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Box,
  Typography,
} from '@mui/material';
import { ContentCopy, CheckCircle, Close } from '@mui/icons-material';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface FormattedQueryProps {
  chartControlState: any;
  propKey: string;
  open: boolean;
  onClose: () => void;
}

const FormattedQuery: React.FC<FormattedQueryProps> = ({
  chartControlState,
  propKey,
  open,
  onClose,
}) => {
  const query = chartControlState?.properties?.[propKey]?.queryResult || '';

  const [copied, setCopied] = useState(false);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      console.error('Clipboard copy failed:', err);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, p: 1.5, bgcolor: 'background.paper' },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider',
            py: 1,
            px: 1,
            pr: 0,
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Formatted SQL Query
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Copy Query" arrow>
              <IconButton
                size="small"
                color={copied ? 'success' : 'default'}
                onClick={() => copyToClipboard(query)}
              >
                {copied ? (
                  <CheckCircle
                    fontSize="small"
                    sx={{
                      color: '#2BB9BB',
                    }}
                  />
                ) : (
                  <ContentCopy fontSize="small" />
                )}
              </IconButton>
            </Tooltip>

            <IconButton size="small" onClick={onClose}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            bgcolor: '#fafafa',
            p: 2,
            maxHeight: '70vh',
            maxWidth: '100%',
            overflowX: 'auto',
            overflowY: 'auto',
          }}
        >
          {query ? (
            <Box
              sx={{
                width: '100%',
                maxHeight: '60vh',
                overflowX: 'auto',
                overflowY: 'auto',
                whiteSpace: 'pre',
              }}
            >
              <SyntaxHighlighter
                className="syntaxHighlight"
                language="sql"
                style={oneLight}
                showLineNumbers={true}
                wrapLines={false}
                lineProps={{ style: { wordBreak: 'break-word', whiteSpace: 'pre-wrap' } }}
              >
                {query}
              </SyntaxHighlighter>
            </Box>
          ) : (
            <Typography color="text.secondary" fontStyle="italic">
              No query result available.
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={copied}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={2000}
      >
        <Alert
          severity="success"
          variant="filled"
          sx={{ borderRadius: 2, backgroundColor: '#2BB9BB' }}
        >
          Query copied to clipboard
        </Alert>
      </Snackbar>
    </>
  );
};

export default FormattedQuery;
